import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAllReasons, getCorrection, getRevertReasons, revertCorrection, searchCorrections } from '../api/dataCorrectionApi'
import { classifyFailure } from '../model/apiFailure'
import { partnerIdOf, stepOf } from '../model/historyGroups'
import { dateErrors, HISTORY_SEARCH_DEBOUNCE_MS, initialHistoryFilters, searchBody, withDay, withFilter } from '../model/historyQuery'
import { describeHistoryFailure, describeRevertFailure } from '../model/messageCatalog'
import { revertBodyOf } from '../model/revertPlan'
import { RETRY_WAIT_MS } from './usePreview'

const NO_MESSAGE = {}
const NO_PARTNER = { id: '', loading: false, detail: undefined, message: NO_MESSAGE }
const NO_DETAIL = { id: '', loading: false, detail: undefined, message: NO_MESSAGE, partner: NO_PARTNER }
const NO_FIELDS = { reason: NO_MESSAGE, comment: NO_MESSAGE }
const NO_REVERT = { action: '', reasonCode: '', comment: '', sending: false, message: NO_MESSAGE, field: NO_FIELDS }
const NO_REVERT_REASONS = { list: [], status: 'idle' }

const isRetry = (error) => {
  const failure = classifyFailure(error)
  return failure.kind === 'contract' && failure.message === 'DATA_CORRECTION_RETRY'
}

/** `request()` again ONCE, after a short wait, when Sun answers DATA_CORRECTION_RETRY (RH-24: at most one retry). */
export const withOneRetry = (request, wait = RETRY_WAIT_MS) => Promise.resolve().then(request).catch((error) => {
  if (!isRetry(error)) throw error
  return new Promise((resolve) => setTimeout(resolve, wait)).then(request)
})

/**
 * What the detail draws (story 211, RN9): `{chosen, close, step}` from the details loaded. `chosen` is the one the user opened;
 * of a two-step correction `close` and `step` are its two details (either may be missing: loading, failed or not known).
 */
export const groupOf = (selected) => {
  const chosen = selected.detail
  if (!chosen) return {}
  const partner = selected.partner.detail
  const kind = stepOf(chosen)
  if (kind === 'CLOSE') return { chosen, close: chosen, step: partner }
  if (kind === 'SINGLE') return { chosen }
  return { chosen, close: partner, step: chosen }
}

/**
 * The «Correction history» tab (#207, #205, and story 211): the filters, the page that was asked for, the list Sun answered, the
 * detail of the selected correction (with the other step of a two-step correction) and the inline confirmation of reverting it.
 * The list is asked for as soon as the screen mounts so that the tab shows its counter.
 * `reloadFirstPage()` goes back to page 0 and asks again (R8: after applying or reverting, a row could repeat or be skipped);
 * `handleSelect(id, partnerId)` opens a detail (and the other step, asked in parallel when the list already knows it, else after
 * the detail says which it is). The reasons (of applying AND of reverting, to name both) are asked for the first time the tab
 * is `active`; the ones of reverting the first time a confirmation opens. `onReverted(result)` runs after a revert answered 200.
 */
export const useHistory = ({ active = false, onReverted } = {}) => {
  const [filters, setFilters] = useState(initialHistoryFilters)
  const [textInput, setTextInput] = useState('')
  const [page, setPage] = useState(0)
  const [reloadToken, setReloadToken] = useState(0)
  const [list, setList] = useState({ loading: true, response: undefined, message: NO_MESSAGE })
  const [selected, setSelected] = useState(NO_DETAIL)
  const [reasons, setReasons] = useState([])
  const [revertReasons, setRevertReasons] = useState(NO_REVERT_REASONS)
  const [revert, setRevert] = useState(NO_REVERT)
  const aliveRef = useRef(false)
  const listSequence = useRef(0)
  const detailSequence = useRef(0)
  const partnerSequence = useRef(0)
  const sendingRef = useRef(false)
  const lastAttempt = useRef({ targetId: '', body: {} })
  const onRevertedRef = useRef(onReverted)
  onRevertedRef.current = onReverted
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  const revertRef = useRef(revert)
  revertRef.current = revert

  useEffect(() => {
    // Switched on inside the effect (StrictMode mounts twice).
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  const reasonsAsked = useRef(false)
  useEffect(() => {
    if (!active || reasonsAsked.current) return
    reasonsAsked.current = true
    // Without them a reason is shown by its code: nothing else depends on them.
    getAllReasons().then((answer) => { if (aliveRef.current && Array.isArray(answer)) setReasons(answer) }, () => {})
  }, [active])

  // «Search» waits 300 ms after the last key before asking (#207 §8.1: Sun does not debounce).
  useEffect(() => {
    if (textInput === filters.text) return undefined
    const timer = setTimeout(() => {
      setFilters((current) => withFilter(current, 'text', textInput))
      setPage(0)
    }, HISTORY_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [textInput, filters.text])

  // RN1: a crossed range is an error next to its field and nothing is searched (Sun would answer 400).
  const errors = useMemo(() => dateErrors(filters), [filters])
  const crossed = Object.keys(errors).length > 0

  useEffect(() => {
    // Only the answer of the LAST request is painted (the same rule as the timeline, RN15).
    const sequence = ++listSequence.current
    if (crossed) return
    const isCurrent = () => aliveRef.current && sequence === listSequence.current
    setList((current) => ({ ...current, loading: true, message: NO_MESSAGE }))
    withOneRetry(() => searchCorrections(searchBody(filters, page))).then((response) => {
      if (!isCurrent()) return
      // `apiPost` resolves undefined when it already sent the app to /connection_error.
      if (response === undefined || !Array.isArray(response.content)) {
        setList((current) => ({ ...current, loading: false }))
        return
      }
      setList({ loading: false, response, message: NO_MESSAGE })
    }).catch((error) => {
      if (!isCurrent()) return
      const failure = classifyFailure(error)
      if (failure.kind === 'aborted') return
      // A 500 of one page keeps the screen (RN24 of #207): the message replaces the rows, the filters and the pager stay.
      setList((current) => ({ loading: false, response: current.response, message: describeHistoryFailure(failure, 'page') }))
    })
  }, [filters, page, reloadToken, crossed])

  // The other step of a two-step correction (RN9): its own request and its own failure, so that the chosen one is still shown.
  // `keepShown`: a reading of what is already drawn, so it is not blanked while it is read again.
  const loadPartner = useCallback((selectionSequence, id, keepShown) => {
    const sequence = ++partnerSequence.current
    const isCurrent = () => aliveRef.current && selectionSequence === detailSequence.current && sequence === partnerSequence.current
    if (!keepShown) setSelected((current) => ({ ...current, partner: { id, loading: true, detail: undefined, message: NO_MESSAGE } }))
    withOneRetry(() => getCorrection(id)).then((detail) => {
      if (!isCurrent()) return
      setSelected((current) => ({ ...current, partner: { id, loading: false, detail, message: NO_MESSAGE } }))
    }).catch((error) => {
      if (!isCurrent()) return
      setSelected((current) => ({ ...current, partner: { id, loading: false, detail: undefined, message: describeHistoryFailure(classifyFailure(error), 'detail') } }))
    })
  }, [])

  // `silent`: read again what is already shown (after a revert) without blanking it.
  const loadDetail = useCallback((id, partnerId, silent) => {
    const sequence = ++detailSequence.current
    const isCurrent = () => aliveRef.current && sequence === detailSequence.current
    const known = partnerId || ''
    const partnerShown = silent && selectedRef.current.partner.detail !== undefined
    if (!silent) setSelected({ id, loading: true, detail: undefined, message: NO_MESSAGE, partner: { ...NO_PARTNER, id: known, loading: known !== '' } })
    if (known) loadPartner(sequence, known, partnerShown)
    withOneRetry(() => getCorrection(id)).then((detail) => {
      if (!isCurrent()) return
      if (detail === undefined) {
        setSelected((current) => ({ ...current, id, loading: false, detail: undefined, message: NO_MESSAGE }))
        return
      }
      setSelected((current) => ({ ...current, id, loading: false, detail, message: NO_MESSAGE }))
      // Not known from the list: the detail says which is the other step (a step 2 ⇒ its close; a close ⇒ its REVERT_CHILD_FIRST).
      const derived = known ? '' : partnerIdOf(detail)
      if (derived) loadPartner(sequence, derived, partnerShown)
    }).catch((error) => {
      if (!isCurrent()) return
      setSelected((current) => ({ ...current, id, loading: false, detail: undefined, message: describeHistoryFailure(classifyFailure(error), 'detail') }))
    })
  }, [loadPartner])

  const changeFilter = useCallback((name, value) => {
    setFilters((current) => withFilter(current, name, value))
    setPage(0)
  }, [])

  // One end of a date range (RN1): the day `yyyy-MM-dd`, '' to empty it.
  // The picker reports every keystroke: an end that does not change asks nothing again.
  const changeDay = useCallback((range, end, day) => {
    setFilters((current) => (current[range][end] === day ? current : withDay(current, range, end, day)))
    setPage(0)
  }, [])

  const reloadFirstPage = useCallback(() => {
    setPage(0)
    setReloadToken((token) => token + 1)
  }, [])

  const retryList = useCallback(() => setReloadToken((token) => token + 1), [])

  // Choosing another correction (the user, or a link) closes the confirmation of the previous one; nothing changes while reverting.
  const select = useCallback((id, partnerId = '') => {
    if (sendingRef.current) return
    setRevert(NO_REVERT)
    loadDetail(id, partnerId, false)
  }, [loadDetail])

  const retryDetail = useCallback(() => {
    const { id, partner } = selectedRef.current
    if (id) loadDetail(id, partner.id, false)
  }, [loadDetail])
  const retryPartner = useCallback(() => {
    const { partner } = selectedRef.current
    if (partner.id) loadPartner(detailSequence.current, partner.id, false)
  }, [loadPartner])
  const clearSelection = useCallback(() => {
    if (sendingRef.current) return
    detailSequence.current += 1
    setRevert(NO_REVERT)
    setSelected(NO_DETAIL)
  }, [])

  // ---- Revert (RN11-RN14) ----

  // The reasons of reverting are asked for the first time a confirmation opens, and again only if they failed.
  const revertReasonsStatus = useRef('idle')
  const askRevertReasons = useCallback(() => {
    if (revertReasonsStatus.current === 'loading' || revertReasonsStatus.current === 'loaded') return
    revertReasonsStatus.current = 'loading'
    setRevertReasons({ list: [], status: 'loading' })
    const failed = () => {
      revertReasonsStatus.current = 'failed'
      if (aliveRef.current) setRevertReasons({ list: [], status: 'failed' })
    }
    getRevertReasons().then((answer) => {
      if (!Array.isArray(answer)) {
        failed()
        return
      }
      revertReasonsStatus.current = 'loaded'
      if (aliveRef.current) setRevertReasons({ list: answer, status: 'loaded' })
    }, failed)
  }, [])

  const openRevert = useCallback((actionId) => {
    if (sendingRef.current) return
    setRevert({ ...NO_REVERT, action: actionId })
    askRevertReasons()
  }, [askRevertReasons])
  const cancelRevert = useCallback(() => { if (!sendingRef.current) setRevert(NO_REVERT) }, [])
  const setRevertReason = useCallback((reasonCode) => setRevert((current) => ({ ...current, reasonCode })), [])
  const setRevertComment = useCallback((comment) => setRevert((current) => ({ ...current, comment })), [])

  const reloadDetails = useCallback(() => {
    const { id, partner } = selectedRef.current
    if (id) loadDetail(id, partner.id, true)
  }, [loadDetail])

  // One request of revert; `automaticRetry` = the one retry after 1 s of a RETRY (the button of a second RETRY sends without it).
  const sendRevert = useCallback((targetId, body, automaticRetry) => {
    if (sendingRef.current) return
    sendingRef.current = true
    lastAttempt.current = { targetId, body }
    setRevert((current) => ({ ...current, sending: true, message: NO_MESSAGE, field: NO_FIELDS }))
    const send = () => revertCorrection(targetId, body)
    const request = automaticRetry ? withOneRetry(send) : Promise.resolve().then(send)
    request.then((result) => {
      sendingRef.current = false
      if (!aliveRef.current) return
      // `apiPost` resolves undefined when it already sent the app to /connection_error.
      if (result === undefined) {
        setRevert((current) => ({ ...current, sending: false }))
        return
      }
      setRevert(NO_REVERT)
      reloadDetails()
      reloadFirstPage()
      if (onRevertedRef.current) onRevertedRef.current(result)
    }).catch((error) => {
      sendingRef.current = false
      if (!aliveRef.current) return
      const failure = classifyFailure(error)
      if (failure.kind === 'aborted') {
        setRevert((current) => ({ ...current, sending: false }))
        return
      }
      const message = describeRevertFailure(failure)
      setRevert((current) => ({
        ...current,
        sending: false,
        message: message.placement === 'confirmation' ? message : NO_MESSAGE,
        field: {
          reason: message.placement === 'reason' ? message : NO_MESSAGE,
          comment: message.placement === 'comment' ? message : NO_MESSAGE
        }
      }))
      if (message.reload !== 'NONE') reloadDetails()
      if (message.reload === 'DETAIL_AND_PAGE') reloadFirstPage()
    })
  }, [reloadDetails, reloadFirstPage])

  // `action` is the one of the plan the confirmation was opened for (it knows the target and the cascade, RN12).
  const confirmRevert = useCallback((action) => {
    const { reasonCode, comment } = revertRef.current
    sendRevert(action.targetId, revertBodyOf({ reasonCode, comment }, action), true)
  }, [sendRevert])
  const retryRevert = useCallback(() => sendRevert(lastAttempt.current.targetId, lastAttempt.current.body, false), [sendRevert])

  const group = useMemo(() => groupOf(selected), [selected])

  return {
    filters,
    textInput,
    page,
    list,
    selected,
    group,
    reasons,
    dateErrors: errors,
    revert,
    revertReasons,
    // While a revert is in flight the list and the filters are locked (RN14).
    locked: revert.sending,
    totalElements: list.response ? list.response.totalElements : 0,
    handleTextInput: setTextInput,
    handleFilter: changeFilter,
    handleDay: changeDay,
    handlePage: setPage,
    handleSelect: select,
    handleClearSelection: clearSelection,
    handleReloadFirstPage: reloadFirstPage,
    handleRetryList: retryList,
    handleRetryDetail: retryDetail,
    handleRetryPartner: retryPartner,
    handleOpenRevert: openRevert,
    handleCancelRevert: cancelRevert,
    handleRevertReason: setRevertReason,
    handleRevertComment: setRevertComment,
    handleConfirmRevert: confirmRevert,
    handleRetryRevert: retryRevert
  }
}
